import Skeleton from '../../components/layout/Skeleton';
import { useQuery } from 'react-query';
import axios from 'axios';
import HBox from '../../components/layout/HBox';
import VBox from '@/components/layout/VBox';
import { translateFilter } from './util';
import { useCallback, useEffect, useState } from 'react';
import Botao from '../../components/Botao';
import { differenceInDays } from 'date-fns';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

const EMOCOES = ['Alegria', 'Entusiasmo', 'Indiferença', 'Raiva', 'Frustração', 'Luto', 'Outros'] as const;

const EMOCAO_CORES: Record<string, string> = {
    'Alegria': '#22c55e',
    'Entusiasmo': '#86efac',
    'Indiferença': '#9ca3af',
    'Raiva': '#ef4444',
    'Frustração': '#f97316',
    'Luto': '#a855f7',
    'Outros': '#6b7280',
};

const parseDate = (v: string) =>
    new Date(v.substring(0, 10) + 'T00:00:00.000').toLocaleDateString();

export const GraficoEmocaoCliente = ({ periodo, filterCanal }: any) => {
    const [foco, setFoco] = useState<string | null>(null);

    const calcBestInterval = () => {
        let d1 = periodo![1],
            d2 = periodo![2];
        if (!d2) {
            d2 = new Date().toISOString().substring(0, 10);
        }
        const days = differenceInDays(d2, d1);
        if (days <= 7) return 'dia';
        if (days <= 31) return 'semana';
        return 'mes';
    };

    const [selectedPeriod, setSelectedPeriod] = useState<'dia' | 'semana' | 'mes'>(calcBestInterval);

    useEffect(() => {
        setSelectedPeriod(calcBestInterval());
    }, [periodo]);

    const CustomTooltip = useCallback(
        ({ active, payload }: any) => {
            if (active && payload && payload.length) {
                const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
                const selectedMetric = payload.find((p: any) => foco === p.dataKey);

                return (
                    <div
                        style={{
                            backgroundColor: '#fff8',
                            border: '1px solid #ccc',
                            padding: '0.5rem',
                        }}
                    >
                        {payload?.[0]?.payload?.periodo && (
                            <p>
                                <b>{parseDate(payload[0].payload.periodo)}</b>
                            </p>
                        )}
                        {selectedMetric ? (
                            <p>
                                {selectedMetric.name}: {total > 0 ? ((selectedMetric.value / total) * 100).toFixed(1) : '0.0'}%
                            </p>
                        ) : (
                            payload.map((p: any) => (
                                <p key={p.dataKey}>
                                    {p.name}: {total > 0 ? ((p.value / total) * 100).toFixed(1) : '0.0'}%
                                </p>
                            ))
                        )}
                    </div>
                );
            }
            return null;
        },
        [foco]
    );

    const q = useQuery(
        ['emocoes_cliente', selectedPeriod, periodo, filterCanal],
        async () => {
            const qs = new URLSearchParams();
            qs.append('facts', 'total');
            qs.append('dimensions', `data_criacao_${selectedPeriod},emocoes_cliente`);
            qs.append('filter', translateFilter({ periodo, canal_id: filterCanal }));
            const res = await axios.get('/api/relatorios/chat_resumo?' + qs.toString());
            return res.data.result;
        },
        { refetchOnWindowFocus: false }
    );

    const dadosAbsolutos = q.data
        ? q.data.reduce((acc: any[], row: any) => {
            const periodo = row[`data_criacao_${selectedPeriod}`].substring(0, 10);
            if (!acc[acc.length - 1] || acc[acc.length - 1].periodo !== periodo) {
                acc.push({
                    periodo,
                    Alegria: 0,
                    Entusiasmo: 0,
                    Indiferença: 0,
                    Raiva: 0,
                    Frustração: 0,
                    Luto: 0,
                    Outros: 0,
                });
            }
            const curr = acc[acc.length - 1];
            const emocao = row.emocoes_cliente;
            if (emocao && Object.prototype.hasOwnProperty.call(curr, emocao)) {
                curr[emocao] = parseInt(row.total);
            }
            return acc;
        }, [])
        : [];

    const totalChats = dadosAbsolutos.reduce((sum: number, d: any) => {
        return sum + EMOCOES.reduce((s, e) => s + (d[e] || 0), 0);
    }, 0);

    const handleMouseEnter = (o: any) => setFoco(o.dataKey);
    const handleMouseLeave = () => setFoco(null);

    if (q.isFetching) return <Skeleton format="box" />;

    if (totalChats < 10) {
        return (
            <VBox center>
                <p
                    style={{
                        fontSize: '0.875rem',
                        color: 'var(--tc-color-gray-500)',
                        textAlign: 'center',
                    }}
                >
                    Sem dados suficientes para o período selecionado.
                </p>
            </VBox>
        );
    }

    return (
        <VBox>
            <HBox>
                <Botao
                    variant={selectedPeriod === 'dia' ? 'outline-primary' : 'outline-secondary'}
                    onClick={() => setSelectedPeriod('dia')}
                >
                    Dia
                </Botao>
                <Botao
                    variant={selectedPeriod === 'semana' ? 'outline-primary' : 'outline-secondary'}
                    onClick={() => setSelectedPeriod('semana')}
                >
                    Semana
                </Botao>
                <Botao
                    variant={selectedPeriod === 'mes' ? 'outline-primary' : 'outline-secondary'}
                    onClick={() => setSelectedPeriod('mes')}
                >
                    Mês
                </Botao>
            </HBox>

            {dadosAbsolutos.length === 0 && (
                <VBox center>
                    <p>Nenhum dado encontrado</p>
                </VBox>
            )}

            {dadosAbsolutos.length > 0 && (
                <>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart
                            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                            data={dadosAbsolutos}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="periodo" tickFormatter={parseDate} />
                            <YAxis />
                            <Tooltip
                                cursor={{ fill: '#8882' }}
                                itemStyle={{ padding: 0, fontSize: '0.9rem' }}
                                labelStyle={{ fontWeight: 'bold' }}
                                content={<CustomTooltip />}
                            />
                            <Legend
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                wrapperStyle={{ height: '60px', overflowY: 'auto' }}
                                height={80}
                                formatter={(v, e: any) => {
                                    const { dataKey } = e;
                                    return (
                                        <span
                                            style={{
                                                color: EMOCAO_CORES[dataKey] ?? '#888',
                                                opacity: !foco || foco === dataKey ? 1 : 0.3,
                                                fontWeight: foco === dataKey ? 'bold' : 'normal',
                                            }}
                                        >
                                            {v}
                                        </span>
                                    );
                                }}
                            />
                            {EMOCOES.map((emocao) => (
                                <Bar
                                    key={emocao}
                                    dataKey={emocao}
                                    name={emocao}
                                    stackId="a"
                                    fill={EMOCAO_CORES[emocao]}
                                    fillOpacity={!foco || foco === emocao ? 0.85 : 0.2}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                    <p
                        style={{
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            color: 'var(--tc-color-gray-500)',
                            textAlign: 'center',
                        }}
                    >
                        Emoção predominante identificada por IA. Consulte as conversas do
                        período para mais detalhes.
                    </p>
                </>
            )}
        </VBox>
    );
};
